
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dumbbell, GraduationCap, Loader2 } from "lucide-react";

const CompleteProfile = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { t } = useLanguage();
  const { getPath } = useLanguagePath();
  const navigate = useNavigate();

  const [accountType, setAccountType] = useState<"trainer" | "student" | null>(null);
  const [firstName, setFirstName] = useState(profile?.first_name || "");
  const [lastName, setLastName] = useState(profile?.last_name || "");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !accountType) return;

    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t("completeProfile.errors.nameRequired"));
      return;
    }

    setLoading(true);
    try {
      const updates: Record<string, any> = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        is_trainer: accountType === "trainer",
        is_student: accountType === "student",
        is_onboarded: true,
        country: country.trim() || null,
        city: city.trim() || null,
      };

      if (accountType === "trainer") {
        updates.subscription_plan = "trial";
        updates.subscription_status = "trialing";
        if (phone.trim()) {
          updates.phone = phone.trim();
        }
      }

      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);

      if (error) throw error;

      await refreshProfile();

      const dashboardPath = accountType === "trainer" ? "/dashboard" : "/student-dashboard";
      navigate(getPath(dashboardPath), { replace: true });
    } catch (error: any) {
      console.error("Error completing profile:", error);
      toast.error(t("completeProfile.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6 bg-card p-8 rounded-lg shadow-lg border border-border">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("completeProfile.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("completeProfile.subtitle")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Type */}
          <div className="space-y-3">
            <Label className="text-foreground font-medium">
              {t("completeProfile.accountType")} *
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAccountType("trainer")}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                  accountType === "trainer"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/50 text-muted-foreground"
                }`}
              >
                <Dumbbell className="h-8 w-8" />
                <span className="text-sm font-medium">
                  {t("completeProfile.trainer")}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setAccountType("student")}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                  accountType === "student"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/50 text-muted-foreground"
                }`}
              >
                <GraduationCap className="h-8 w-8" />
                <span className="text-sm font-medium">
                  {t("completeProfile.student")}
                </span>
              </button>
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-foreground">
                {t("completeProfile.firstName")} *
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("completeProfile.firstNamePlaceholder")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-foreground">
                {t("completeProfile.lastName")} *
              </Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("completeProfile.lastNamePlaceholder")}
                required
              />
            </div>
          </div>

          {/* Country & City */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="country" className="text-foreground">
                {t("completeProfile.country")}
              </Label>
              <Input
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder={t("completeProfile.countryPlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city" className="text-foreground">
                {t("completeProfile.city")}
              </Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("completeProfile.cityPlaceholder")}
              />
            </div>
          </div>

          {/* Phone (trainer only) */}
          {accountType === "trainer" && (
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-foreground">
                {t("completeProfile.phone")}
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("completeProfile.phonePlaceholder")}
              />
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !accountType}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t("completeProfile.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;
